import { formatLine } from './lineDataUtils';

export const getPeriodSuffix = (periodNum) => {
  switch (periodNum) {
    case 1: return 'st';
    case 2: return 'nd';
    case 3: return 'rd';
    default: return 'th';
  }
};

export const formatGamePeriod = (clock, period) => {
  const periodNum = parseInt(period, 10);
  if (!periodNum || Number.isNaN(periodNum)) return '';

  if (clock === '00:00') {
    switch (periodNum) {
      case 1: return 'End of 1st';
      case 2: return 'Halftime';
      case 3: return 'End of 3rd';
      case 4: return 'End of 4th';
      default: return 'Final';
    }
  }

  return `${clock} ${periodNum}${getPeriodSuffix(periodNum)}`;
};

export const formatBettingLine = (value) => {
  if (value === null || value === undefined || value === 'null') return 'N/A';
  if (value === 0) return 'N/A';
  return formatLine(value);
};

export const calculatePickMargin = (pickType, lockLine, homeScore, awayScore) => {
  const home = homeScore || 0;
  const away = awayScore || 0;
  const totalScore = home + away;
  switch (pickType) {
    case 'home':
      return (home - away) + lockLine;
    case 'away':
      return (away - home) + lockLine;
    case 'over':
      return totalScore - lockLine;
    case 'under':
      return lockLine - totalScore;
    default:
      return 0;
  }
};

export const getPickLabel = (pickType, game) => {
  switch (pickType) {
    case 'home':
      return game.home_team.abbreviation || game.home_team.name;
    case 'away':
      return game.away_team.abbreviation || game.away_team.name;
    case 'over':
      return 'Over';
    case 'under':
      return 'Under';
    default:
      return '';
  }
};

export const formatLockLineDisplay = (lockLine, isOverUnder) => {
  if (isOverUnder) return lockLine;
  return lockLine >= 0 ? `+${lockLine}` : `${lockLine}`;
};

export const getMarginColorClass = (margin, styles) => {
  if (margin > 0) return styles.winning;
  if (margin < 0) return styles.losing;
  return styles.push;
};

export const formatLineMovement = (openLine, currentLine) => {
  if (openLine == null || currentLine == null) return null;
  if (openLine === currentLine) return 'unchanged';
  return currentLine > openLine ? 'up' : 'down';
};

export const QUARTER_LABELS = ['Q1', 'Q2', 'Q3', 'Q4', 'OT'];

const COMPASS_16 = [
  'N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE',
  'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW',
];

/** Convert meteorological degrees (0–360) to a 16-point compass label. */
export const formatWindCompass = (degrees) => {
  const num = parseFloat(degrees);
  if (Number.isNaN(num)) return null;
  const index = Math.round(num / 22.5) % 16;
  return COMPASS_16[index];
};

export const getQuarterState = (quarterIndex, period, clock, gameStatus) => {
  const quarterNum = quarterIndex + 1;
  const periodNum = parseInt(period, 10);

  if (gameStatus === 'scheduled') return 'future';
  if (gameStatus === 'completed') return 'completed';
  if (!periodNum || Number.isNaN(periodNum)) return 'future';

  if (quarterNum < periodNum) return 'completed';
  if (quarterNum > periodNum) return 'future';
  if (clock === '00:00') return 'completed';
  return 'active';
};

const WEATHER_ICON_RULES = [
  { pattern: /thunder|storm|lightning/i, icon: '⛈️' },
  { pattern: /snow|flurr|blizzard/i, icon: '❄️' },
  { pattern: /rain|shower|drizzle/i, icon: '🌧️' },
  { pattern: /fog|mist|haze/i, icon: '🌫️' },
  { pattern: /partly|fair|mostly/i, icon: '⛅' },
  { pattern: /cloud|overcast/i, icon: '☁️' },
  { pattern: /clear|sunny|sun/i, icon: '☀️' },
];

export const getWeatherIcon = (condition) => {
  if (!condition) return '🌡️';
  const match = WEATHER_ICON_RULES.find(({ pattern }) => pattern.test(condition));
  return match?.icon ?? '🌤️';
};

export const formatWeatherContext = (game, displayDate, displayTime) => {
  const status = game.game_status;
  const kickoff = displayTime && displayDate
    ? `${displayTime} kickoff on ${displayDate}`
    : 'kickoff';

  if (status === 'scheduled') {
    return `Forecast for ${kickoff}.`;
  }
  if (status === 'in_progress') {
    return 'Current conditions from the live scoreboard feed.';
  }
  if (status === 'completed') {
    return `Conditions recorded at ${kickoff}.`;
  }
  return null;
};

export const formatForecastAge = (refreshedAt) => {
  if (!refreshedAt) return null;
  const refreshed = new Date(refreshedAt.includes(' ') && !refreshedAt.includes('T')
    ? `${refreshedAt.replace(' ', 'T')}Z`
    : refreshedAt);
  if (Number.isNaN(refreshed.getTime())) return null;

  const hoursAgo = Math.floor((Date.now() - refreshed.getTime()) / (1000 * 60 * 60));
  if (hoursAgo < 1) return 'Updated within the last hour';
  if (hoursAgo < 24) return `Forecast data updated ${hoursAgo}h ago`;
  const daysAgo = Math.floor(hoursAgo / 24);
  return `Forecast data updated ${daysAgo}d ago`;
};
